import { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { getTokenStore } from '@shared/api/token-store';
import { useMe } from '@shared/hooks/use-me';
import { useAuthStore } from '@shared/stores/auth-store';
import {
  requestAndRegisterPushToken,
  setupForegroundNotificationListener,
} from '@shared/services/firebase-push';
import { AssociateSidebar } from './associate-sidebar';
import { AssociateTopbar } from './associate-topbar';
import { KycBanner } from './kyc-banner';
import { KycGate } from './kyc-gate';
import { FeedbackWidget } from '@/components/layout/feedback-widget';

export function AssociateLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem('associate_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('associate_sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  };

  const hasToken = !!getTokenStore().getAccessToken();
  const { data: me, isLoading, isError } = useMe();
  const hydrated = useAuthStore((s) => s.hydrated);

  useEffect(() => {
    if (hasToken) {
      void requestAndRegisterPushToken();
      const cleanup = setupForegroundNotificationListener();
      return () => cleanup();
    }
  }, [hasToken]);

  if (!hydrated || isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  if (!hasToken) {
    return <Navigate to="/login" replace />;
  }
  if (isError) {
    getTokenStore().clear();
    return <Navigate to="/login" replace />;
  }
  // Role gate — only ASSOCIATE or FRANCHISE see this shell.
  if (me && me.role !== 'ASSOCIATE' && me.role !== 'FRANCHISE') {
    const fallback = me.role === 'ADMIN' ? '/admin' : '/client/home';
    return <Navigate to={fallback} replace />;
  }

  return (
    <div className="flex h-screen h-[100dvh] min-h-0 w-full overflow-hidden bg-gray-50">
      <AssociateSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={isCollapsed}
        onToggleCollapse={toggleCollapsed}
      />
      <div className="flex min-w-0 flex-1 flex-col h-full min-h-0 overflow-hidden">
        <AssociateTopbar onMenuClick={() => setSidebarOpen(true)} />
        <KycBanner />
        <main className="min-w-0 flex-1 overflow-y-auto flex flex-col min-h-0">
          <Outlet />
        </main>
      </div>
      <KycGate />
      <FeedbackWidget />
    </div>
  );
}
